import { useAuth0 } from '@auth0/auth0-react';
import React, { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLazyGetDirectAccessAccountsQuery } from 'services/accounts';

import RoutePath from 'router/path';

import selectAccount, { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { setAccount } from 'state/slices/accountSlice';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel, AccountInfo, AccountType } from 'types/account';
import { UserAccessLevel } from 'types/user';

import { Breadcrumbs } from '../../breadcrumbs';
import { BreadcrumbsItem } from '../../breadcrumbs/types';
import OptionallyVisible from '../../optionallyVisible';
import { Typography } from '../../typography';
import { CreateAccountModal } from '../createAccount';
import { useCreateAccountModal } from '../createAccount/useCreateAccountModal';
import { ProfileMenu } from '../profileMenu';
import {
  ContentWrapper,
  Header,
  ProfileMenuContainer,
  TestEnvironmentBadge,
  TestEnvironmentIndicator,
  Wrapper,
} from './pageWrapper.styles';

export interface PageWrapperProps {
  pageTitle?: string;
  breadcrumbs: BreadcrumbsItem[];
}

const TEST_MODE_LABEL = ['app.testMode', 'Test mode'];
const isTestMode = Boolean(window.s4cConfig.isTestEnvironment);

export const PageWrapper: React.FC<PropsWithChildren<PageWrapperProps>> = ({ children, pageTitle, breadcrumbs }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { logout } = useAuth0();
  const { account } = useSelector(selectAccount);
  const [fetchDirectAccessAccounts, { data: directAccessAccountsData }] = useLazyGetDirectAccessAccountsQuery();
  const dispatch = useDispatch();
  const userLevel = useSelector(selectUserLevel);
  const accountAccessLevel = useSelector(selectAccountAccess);

  const isPartner = userLevel === UserAccessLevel.Partner;

  useEffect(() => {
    if (isPartner) {
      fetchDirectAccessAccounts();
    }
  }, [isPartner, fetchDirectAccessAccounts]);

  const selectedAccount = isPartner ? account : undefined;
  const availableAccounts = isPartner ? directAccessAccountsData?.data?.map((record) => record.account) || [] : [];
  const isPartnerWithPspOrGroupAccount = isPartner && (account.type === AccountType.PSP || account.type === AccountType.Pwc_Group);
  const isSelectedAccountViewer = accountAccessLevel === AccountAccessLevel.Viewer;
  const isAccountCreationAllowed =
    userLevel === UserAccessLevel.SuperAdmin ||
    userLevel === UserAccessLevel.Admin ||
    (isPartnerWithPspOrGroupAccount && !isSelectedAccountViewer);

  const { handleCreateAccountModalOpen, createdAccount, modalProps: createAccountModalProps } = useCreateAccountModal();

  const handleCreateAccount = () => {
    if (isAccountCreationAllowed) {
      handleCreateAccountModalOpen();
    }
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleAccountSwitch = (nextAccount: AccountInfo) => {
    const { account, access } = directAccessAccountsData?.data?.find((record) => record.account.uuid === nextAccount.uuid) || {};
    if (account && access) {
      dispatch(setAccount({ account, access }));
    }
  };

  const navigateToProfile = () => {
    navigate(RoutePath.Profile.Root);
  };

  return (
    <Wrapper>
      <ContentWrapper>
        <Header>
          <Breadcrumbs items={breadcrumbs} />
          <ProfileMenuContainer>
            <OptionallyVisible visible={isTestMode}>
              <TestEnvironmentBadge>{t(TEST_MODE_LABEL)}</TestEnvironmentBadge>
            </OptionallyVisible>
            <ProfileMenu
              logout={handleLogout}
              selectedAccount={selectedAccount}
              availableAccounts={availableAccounts}
              navigateToProfile={navigateToProfile}
              onAccountSwitch={handleAccountSwitch}
              createAccount={handleCreateAccount}
              isAccountCreationAllowed={isAccountCreationAllowed}
            />
          </ProfileMenuContainer>
        </Header>
        <OptionallyVisible visible={isTestMode}>
          <TestEnvironmentIndicator />
        </OptionallyVisible>
        <OptionallyVisible visible={Boolean(pageTitle)}>
          <Typography variant="caption">{pageTitle}</Typography>
        </OptionallyVisible>
        {children}
      </ContentWrapper>
      <OptionallyVisible visible={Boolean(isAccountCreationAllowed && createdAccount)}>
        <CreateAccountModal {...createAccountModalProps} />
      </OptionallyVisible>
    </Wrapper>
  );
};

export default PageWrapper;
